import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SetupPanel } from "./SetupPanel";
import { SafePage } from "../components/SafePage";
import { PhaseBadge } from "../components/PhaseBadge";
import { AppButton } from "../components/AppButton";
import { AppLink } from "../components/AppLink";
import { ErrorBanner } from "../components/PageBanner";
import { PageLoading } from "../components/PageLoading";
import { HomeV6Body } from "../components/HomeV6Body";
import { readEmbedViaAppBridge } from "../lib/embed";
import {
  applySetup,
  bootstrapShop,
  createPreviewLink,
  getEmbedStatus,
  getMarkets,
  getStats,
  getStatus,
  listRules,
  logReviewRequest,
  onlineStorePreferencesDeepLink,
  publishConfig,
  setEmbedActive,
  syncBilling,
  themeEmbedDeepLink,
  toastPublishResult,
  updateSettings,
} from "../lib/safegeo-api";
import { countryLabel, destinationLabel } from "../lib/format";
import { queryKeys } from "../lib/query-keys";
import { showToast } from "../lib/feedback";
import {
  availableMonths,
  rollingDayOptions,
  statsPeriodKey,
  type StatsPeriod,
} from "../lib/stats-period";

const API_KEY = import.meta.env.VITE_SHOPIFY_API_KEY || "";

function nextCalendarMonthLabel(): string {
  const d = new Date();
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return next.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function thisCalendarMonthLabel(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

type CountryRow = {
  key: string;
  country: string;
  title: string;
  summary: string;
  href: string;
};

export function HomePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [bootstrapped, setBootstrapped] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [setupBusy, setSetupBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [prefsChecked, setPrefsChecked] = useState(() => {
    try {
      return localStorage.getItem("sg_prefs_checked") === "1";
    } catch {
      return false;
    }
  });
  /** Metrics period - Last N days (v6 Activity Live = last 7 days). */
  const [statsPeriod] = useState<StatsPeriod>({
    kind: "days",
    days: 7,
  });
  /** Preview chip + title-bar Open store in test share this country. */
  const [visitorPreviewCc, setVisitorPreviewCc] = useState("CA");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await bootstrapShop({});
        const planHandle = params.get("plan_handle");
        const planKey = params.get("plan_key");
        const chargeId = params.get("charge_id");
        if (planHandle || planKey || chargeId) {
          try {
            await syncBilling({
              plan_key: planKey || planHandle || "safegeo-starter",
              billing_interval:
                (params.get("interval") as "month" | "year") || "year",
              plan_handle: planHandle || undefined,
            });
          } catch {
            /* non-blocking */
          }
        }
        if (!cancelled) setBootstrapped(true);
      } catch (e) {
        if (!cancelled) {
          setBootError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  const statusQ = useQuery({
    queryKey: queryKeys.status,
    queryFn: getStatus,
    enabled: bootstrapped,
  });
  const marketsQ = useQuery({
    queryKey: queryKeys.markets,
    queryFn: getMarkets,
    enabled: bootstrapped,
  });
  const rulesQ = useQuery({
    queryKey: queryKeys.rules,
    queryFn: listRules,
    enabled: bootstrapped,
  });
  const statsMax =
    statusQ.data?.entitlements?.stats_days &&
    statusQ.data.entitlements.stats_days > 0
      ? statusQ.data.entitlements.stats_days
      : 30;
  const dayChoices = rollingDayOptions(statsMax);
  const monthChoices = availableMonths(statsMax);
  const effectivePeriod: StatsPeriod =
    statsPeriod.kind === "days"
      ? {
          kind: "days",
          days: Math.min(
            statsPeriod.days,
            dayChoices[dayChoices.length - 1] || statsMax,
          ),
        }
      : monthChoices.includes(statsPeriod.month)
        ? statsPeriod
        : { kind: "days", days: dayChoices[0] || 7 };
  const periodOpts =
    effectivePeriod.kind === "month"
      ? { month: effectivePeriod.month }
      : { days: effectivePeriod.days };
  /** Activity · Live tiles */
  const liveStatsQ = useQuery({
    queryKey: queryKeys.stats(statsPeriodKey(effectivePeriod), "live"),
    queryFn: () => getStats({ ...periodOpts, traffic: "live" }),
    enabled: bootstrapped && !!statusQ.data,
  });
  /** Activity · Test tiles (v7 §16.3 - preview / geo_test only) */
  const previewStatsQ = useQuery({
    queryKey: queryKeys.stats(statsPeriodKey(effectivePeriod), "preview"),
    queryFn: () => getStats({ ...periodOpts, traffic: "preview" }),
    enabled: bootstrapped && !!statusQ.data,
  });

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["safegeo"] });
  }, [qc]);

  const syncEmbed = useCallback(async () => {
    setChecking(true);
    try {
      let active = await readEmbedViaAppBridge();
      if (active === null) {
        const nest = await getEmbedStatus();
        active = nest.embed_active;
      }
      if (active !== null) {
        await setEmbedActive(active);
        refresh();
      }
    } catch {
      /* silent - setup section surfaces embed status */
    } finally {
      setChecking(false);
    }
  }, [refresh]);

  useEffect(() => {
    if (!bootstrapped) return;
    void syncEmbed();
    const onVis = () => {
      if (document.visibilityState === "visible") void syncEmbed();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [bootstrapped, syncEmbed]);

  useEffect(() => {
    const status = statusQ.data;
    if (!status?.embed_active) return;
    if ((status.redirects_month ?? 0) < 20) return;
    const created = status.created_at
      ? new Date(status.created_at).getTime()
      : 0;
    if (!created || Date.now() - created < 24 * 3600 * 1000) return;
    try {
      const last = Number(localStorage.getItem("sg_review_at") || "0");
      if (last && Date.now() - last < 60 * 864e5) return;
    } catch {
      return;
    }
    if (!window.shopify?.reviews?.request) return;
    void window.shopify.reviews
      .request()
      .then(() => {
        try {
          localStorage.setItem("sg_review_at", String(Date.now()));
        } catch {
          /* ignore */
        }
        void logReviewRequest().catch(() => undefined);
      })
      .catch(() => undefined);
  }, [statusQ.data]);

  if (bootError) {
    return (
      <SafePage screen="home" heading="Home">
        <ErrorBanner heading="Can't load your shop">{bootError}</ErrorBanner>
      </SafePage>
    );
  }

  if (!bootstrapped || statusQ.isLoading) {
    return (
      <SafePage screen="home" heading="Home">
        <PageLoading label="Loading your shop…" />
      </SafePage>
    );
  }

  if (statusQ.error) {
    return (
      <SafePage screen="home" heading="Home">
        <ErrorBanner heading="Status error">
          {(statusQ.error as Error).message}
        </ErrorBanner>
      </SafePage>
    );
  }

  const status = statusQ.data!;
  if (status.needs_plan_pick) {
    return <Navigate to="/app/plans" replace />;
  }

  const embedOn = !!status.embed_active;
  const step2Done = !!status.settings?.setup_step2_at;

  // Markets apply lives on Home checklist (polaris). SetupPanel only when
  // merchant explicitly opens the fuller guide (US-only / Finish setup).
  if (showSetupGuide) {
    return (
      <SetupPanel
        status={status}
        markets={
          marketsQ.data || {
            countries: [],
            labels: [],
            currencies: {},
            count: 1,
            shopify_plus: false,
          }
        }
        embedOn={embedOn}
        checking={checking}
        onSyncEmbed={() => void syncEmbed()}
        onRefresh={refresh}
        onBackToHome={() => setShowSetupGuide(false)}
      />
    );
  }

  const isPaused = !!status.settings?.paused;
  const marketsAuto = !!status.settings?.markets_auto;
  const marketsEnabled = !!status.settings?.markets_enabled;
  const marketCodes =
    status.settings?.market_countries?.length
      ? status.settings.market_countries
      : marketsQ.data?.countries || [];
  const currencies = marketsQ.data?.currencies || {};
  const customRules = (rulesQ.data?.items ?? []).filter(
    (r) => !r.is_fallback && r.is_enabled,
  );

  const countryRows: CountryRow[] = [];
  if (marketCodes.length > 1) {
    marketCodes.slice(1).forEach((code) => {
      const label = countryLabel(code);
      const cur = currencies[code];
      countryRows.push({
        key: `m-${code}`,
        country: code,
        title: label,
        summary: marketsEnabled
          ? destinationLabel(code, cur)
          : "Market redirect off - save on Market redirect to go live",
        href: "/app/market",
      });
    });
  }
  for (const rule of customRules) {
    const codes = rule.countries?.length ? rule.countries : ["??"];
    for (const code of codes) {
      const label = countryLabel(code);
      let summary = "Asked first";
      if (rule.action === "redirect") {
        summary = rule.destination_url
          ? `Send shoppers to ${rule.destination_url}`
          : "Send shoppers to another store";
      } else if (rule.action === "block") {
        summary = "Block browsing on this store";
      } else if (rule.destination_url) {
        summary = `Ask first - suggest ${rule.destination_url}`;
      } else {
        summary = "Ask first - suggest another store";
      }
      countryRows.push({
        key: `r-${rule.id}-${code}`,
        country: code,
        title: label,
        summary,
        href:
          rule.action === "block"
            ? `/app/block/${rule.id}`
            : `/app/stores/${rule.id}`,
      });
    }
  }

  const hasCountries = countryRows.length > 0;

  const embedEditorUrl =
    status.shop_domain && API_KEY
      ? themeEmbedDeepLink(status.shop_domain, API_KEY)
      : null;
  const prefsUrl = status.shop_domain
    ? onlineStorePreferencesDeepLink(status.shop_domain)
    : null;
  const redirectsUsed = status.redirects_month ?? 0;
  const redirectCap = status.redirects_cap;
  const previewTotals = previewStatsQ.data?.totals;
  const liveTotals = liveStatsQ.data?.totals;
  const previewByCountry = previewStatsQ.data?.by_country ?? [];
  const liveByCountry = liveStatsQ.data?.by_country ?? [];
  const previewVisitors =
    previewByCountry.length > 0
      ? previewByCountry.reduce((a, r) => a + Number(r.visitors || 0), 0)
      : (previewTotals?.unique_users ?? 0);
  const previewSuggested = Number(previewTotals?.suggested ?? 0);
  const previewRedirected = Number(previewTotals?.redirected ?? 0);
  /**
   * v7 §16.3 Test tiles (is_test=true rollup):
   * - Previews = visit pings
   * - Would get = ask popups (suggested) + silent auto (redirected)
   * - Popups shown = suggested only
   * - Automatic redirects = redirected (auto only; does NOT burn plan)
   *
   * Nest: redirected column is auto-only (market|redirect). Accept no longer
   * bumps redirected - so Would get does not double-count Switch.
   */
  const previewWouldAct = previewSuggested + previewRedirected;
  const previewPopupsShown = previewSuggested;
  const previewAutoCount = previewRedirected;
  const liveRedirected = Number(liveTotals?.redirected ?? 0);
  const liveSuggested = Number(liveTotals?.suggested ?? 0);
  const liveStayed = Number(liveTotals?.stayed ?? 0);
  const liveSwitched = Number(liveTotals?.switched ?? 0);
  /** Live Automatic redirects = silent market/redirect (is_test=false). */
  const liveAutoCount = liveRedirected;

  const published = !!status.last_published_at;
  const usOnly = marketCodes.length < 2;
  /** Step 2: multi-market needs Market redirect live; US-only can mark via destinations. */
  const countriesChecked = usOnly
    ? step2Done || hasCountries
    : step2Done || marketsEnabled;
  /**
   * v6 Home: Publish gated on 3 Finish setup steps (embed, prefs, markets).
   * Phase: draft = not published; test = published + paused; live = published + !paused.
   */
  const setupReady = embedOn && prefsChecked && countriesChecked;
  const phase: "draft" | "test" | "live" = !published
    ? "draft"
    : isPaused
      ? "test"
      : "live";

  async function openStorefrontTest(cc?: string) {
    const code = (cc || visitorPreviewCc || "CA").toUpperCase().slice(0, 2);
    setVisitorPreviewCc(code);
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const link = await createPreviewLink(code);
      window.open(link.url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => refresh(), 2500);
      window.setTimeout(() => refresh(), 8000);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewBusy(false);
    }
  }

  async function switchPhase(next: "live" | "test") {
    setPublishBusy(true);
    setPreviewError(null);
    try {
      await updateSettings({ paused: next === "test" });
      try {
        await publishConfig();
      } catch {
        /* ok */
      }
      showToast(next === "live" ? "SafeGeo is live" : "Test mode on");
      refresh();
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishBusy(false);
    }
  }

  async function saveMarketsSetup() {
    setSetupBusy(true);
    setPreviewError(null);
    try {
      await applySetup({
        preset: "market",
        market_countries: marketCodes,
        markets_count: Math.max(1, marketsQ.data?.count || marketCodes.length),
      });
      refresh();
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setSetupBusy(false);
    }
  }

  async function publishToStorefront() {
    setPublishBusy(true);
    setPreviewError(null);
    try {
      // First publish → Live (visitors see SafeGeo). Merchant can Switch to Test later.
      await updateSettings({ paused: false });
      const result = await publishConfig({ first_publish: true });
      const pub = toastPublishResult(result, "SafeGeo is live");
      if (!pub.ok && pub.message) setPreviewError(pub.message);
      refresh();
    } catch (e) {
      setPreviewError(
        e instanceof Error ? e.message : "Couldn’t publish to storefront",
      );
    } finally {
      setPublishBusy(false);
    }
  }

  function markPrefsChecked() {
    try {
      localStorage.setItem("sg_prefs_checked", "1");
    } catch {
      /* ignore */
    }
    setPrefsChecked(true);
  }

  const ruleCountrySet = new Set<string>();
  marketCodes.slice(1).forEach((c) => ruleCountrySet.add(c.toUpperCase()));
  for (const rule of customRules) {
    for (const c of rule.countries || []) ruleCountrySet.add(c.toUpperCase());
  }
  const liveVisitorsTotal =
    liveByCountry.length > 0
      ? liveByCountry.reduce((a, r) => a + Number(r.visitors || 0), 0)
      : (liveTotals?.unique_users ?? 0);
  const eligibleTotal = liveByCountry
    .filter((r) => ruleCountrySet.has(String(r.country || "").toUpperCase()))
    .reduce((a, r) => a + Number(r.visitors || 0), 0);

  function explainPreview(cc: string): { result: string; why: string[] } {
    const code = cc.toUpperCase().slice(0, 2);
    const label = countryLabel(code);
    const home = marketCodes[0]?.toUpperCase();
    const inMarket = marketCodes.some(
      (c) => c.toUpperCase().slice(0, 2) === code,
    );
    const blockRule = customRules.find(
      (r) =>
        r.action === "block" &&
        (r.countries || []).some((c) => c.toUpperCase() === code),
    );
    const storeRule = customRules.find(
      (r) =>
        r.action !== "block" &&
        (r.countries || []).some((c) => c.toUpperCase() === code),
    );
    if (blockRule) {
      return {
        result: `${label} visitor sees your Block page.`,
        why: [
          "Skip list: not skipped",
          "Block: matching country rule",
          "Result: blocked page",
        ],
      };
    }
    if (storeRule) {
      return {
        result: marketsAuto
          ? `${label} visitor is sent to another store or website.`
          : `${label} visitor is asked to go to another store or website.`,
        why: [
          "Skip list: not skipped",
          "Block: no rule",
          `Store redirect: ${marketsAuto ? "Send automatically" : "Ask first"}`,
          "Result: popup or redirect",
        ],
      };
    }
    if (home && code === home) {
      return {
        result: `${label} visitor is already in the right market.`,
        why: [
          "Skip list: not skipped",
          "Block and Store: no rules",
          `Market redirect: ${label} is the primary market`,
          "Result: nothing to switch",
        ],
      };
    }
    if (inMarket) {
      const cur = currencies[code];
      return {
        result: marketsAuto
          ? `${label} visitor switches currency${cur ? ` to ${cur}` : ""} automatically.`
          : `${label} visitor is asked to switch${cur ? ` to ${cur}` : ""}.`,
        why: [
          "Skip list: not skipped",
          "Block: no rule",
          "Store redirect: no rule",
          `Market redirect: ${label} market, ${marketsAuto ? "Switch automatically" : "Ask first"}`,
          "Result: " + (marketsAuto ? "automatic switch" : "popup shown"),
        ],
      };
    }
    return {
      result: `${label} visitor sees your store as normal.`,
      why: [
        "Skip list: not skipped",
        "Block: no rule",
        "Store redirect: no rule",
        "Market redirect: no market for this country",
        "Result: nothing happens",
      ],
    };
  }

  const setupDoneCount =
    (embedOn ? 1 : 0) + (prefsChecked ? 1 : 0) + (countriesChecked ? 1 : 0);

  const testPreviews = Number(previewVisitors || 0);
  const testWouldPopup = Number(previewWouldAct || 0);
  const testPopupsShown = Number(previewPopupsShown || 0);

  const homePrimary =
    phase === "draft" ? (
      <AppButton
        slot="primary-action"
        disabled={!setupReady || publishBusy || setupBusy}
        loading={publishBusy || setupBusy}
        commandFor="sg-confirm-publish"
        command="--show"
      >
        Publish
      </AppButton>
    ) : phase === "test" ? (
      <AppButton
        slot="primary-action"
        commandFor="sg-confirm-live"
        command="--show"
      >
        Switch to Live
      </AppButton>
    ) : null;

  return (
    <SafePage
      screen="home"
      heading="Home"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={homePrimary}
      secondaryActions={
        <>
          <AppButton
            slot="secondary-actions"
            disabled={previewBusy}
            onClick={() => void openStorefrontTest(visitorPreviewCc)}
          >
            Open store in test
          </AppButton>
          {phase === "live" ? (
            <AppButton
              slot="secondary-actions"
              commandFor="sg-confirm-test"
              command="--show"
            >
              Switch to Test mode
            </AppButton>
          ) : null}
        </>
      }
    >
      {status.theme_changed && embedEditorUrl ? (
        <s-banner tone="warning" heading="Theme changed - turn SafeGeo on again">
          App embeds don’t always carry over when you publish a new theme.{" "}
          <AppLink href={embedEditorUrl}>Turn on in theme</AppLink>
        </s-banner>
      ) : null}

      {previewError ? (
        <ErrorBanner heading="Couldn’t update">{previewError}</ErrorBanner>
      ) : null}

      <HomeV6Body
        phase={phase}
        shopLabel={
          status.primary_domain || status.shop_domain || "your store"
        }
        marketCount={Math.max(marketCodes.length, marketsQ.data?.count || 0)}
        setupSteps={[
          {
            id: "embed",
            title: "Turn on the SafeGeo app embed",
            done: embedOn,
            btn: embedEditorUrl ? "Open" : "Check",
            how: "In Shopify admin go to Online Store → Themes → Customize. Open App embeds, turn on SafeGeo, then Save.",
            onAct: () => {
              if (embedEditorUrl) window.open(embedEditorUrl, "_top");
              void syncEmbed();
            },
          },
          {
            id: "prefs",
            title: "Turn off Shopify’s own automatic redirection",
            done: prefsChecked,
            btn: "Show me how",
            how: "Shopify can redirect visitors before SafeGeo runs. Open Online Store → Preferences and turn Automatic redirection off.",
            onAct: () => {
              if (prefsUrl) window.open(prefsUrl, "_blank", "noopener");
              markPrefsChecked();
            },
          },
          {
            id: "markets",
            title: usOnly
              ? "Review your markets (add a second market in Shopify)"
              : `Review what happens in each of your ${Math.max(2, marketCodes.length)} markets`,
            done: countriesChecked,
            btn: "Go to Market redirect",
            onAct: () => {
              if (!countriesChecked && embedOn && !usOnly) {
                void saveMarketsSetup();
              }
              navigate("/app/market");
            },
          },
        ]}
        setupDoneCount={setupDoneCount}
        setupReady={setupReady}
        redirectsUsed={redirectsUsed}
        redirectCap={redirectCap}
        monthLabel={thisCalendarMonthLabel()}
        resetsLabel={nextCalendarMonthLabel()}
        testPreviews={testPreviews}
        testWouldPopup={testWouldPopup}
        testPopups={testPopupsShown}
        testAuto={previewAutoCount}
        liveVisitors={liveVisitorsTotal}
        liveEligible={eligibleTotal}
        livePopups={liveSuggested}
        liveAuto={liveAutoCount}
        liveChoseAnother={liveSwitched}
        liveStayedOrClosed={liveStayed}
        explainPreview={explainPreview}
        previewCc={visitorPreviewCc}
        onPreviewCcChange={setVisitorPreviewCc}
        onRefreshShopify={() => {
          void marketsQ.refetch();
          refresh();
          showToast("Refreshing from Shopify…");
        }}
      />

      <s-modal id="sg-confirm-publish" heading="Publish SafeGeo?">
        <s-paragraph>
          This is the first time visitors will see it. From now on you can
          switch between Test and Live yourself, and changes you save apply
          automatically.
        </s-paragraph>
        <AppButton
          slot="secondary-actions"
          commandFor="sg-confirm-publish"
          command="--hide"
        >
          Not yet
        </AppButton>
        <AppButton
          slot="primary-action"
          commandFor="sg-confirm-publish"
          command="--hide"
          loading={publishBusy}
          onClick={() => void publishToStorefront()}
        >
          Publish
        </AppButton>
      </s-modal>

      <s-modal id="sg-confirm-live" heading="Switch to Live?">
        <s-paragraph>
          Visitors from countries with a rule will start seeing popups and
          redirects.
        </s-paragraph>
        <AppButton
          slot="secondary-actions"
          commandFor="sg-confirm-live"
          command="--hide"
        >
          Cancel
        </AppButton>
        <AppButton
          slot="primary-action"
          commandFor="sg-confirm-live"
          command="--hide"
          loading={publishBusy}
          onClick={() => void switchPhase("live")}
        >
          Switch to Live
        </AppButton>
      </s-modal>

      <s-modal id="sg-confirm-test" heading="Switch to Test mode?">
        <s-paragraph>
          Visitors stop being asked, redirected or blocked. Your rules and
          popup are kept.
        </s-paragraph>
        <AppButton
          slot="secondary-actions"
          commandFor="sg-confirm-test"
          command="--hide"
        >
          Stay live
        </AppButton>
        <AppButton
          slot="primary-action"
          commandFor="sg-confirm-test"
          command="--hide"
          loading={publishBusy}
          onClick={() => void switchPhase("test")}
        >
          Switch to Test mode
        </AppButton>
      </s-modal>

      <s-modal id="how-check" heading="Let SafeGeo ask first">
        <s-paragraph>
          Shopify’s Automatic redirection moves shoppers to a market with no
          choice. SafeGeo replaces that with ask first + remember Stay /
          Switch.
        </s-paragraph>
        <s-ordered-list>
          <s-list-item>
            Open{" "}
            {prefsUrl ? (
              <AppLink href={prefsUrl}>Online Store › Preferences</AppLink>
            ) : (
              <s-text type="strong">Online Store › Preferences</s-text>
            )}
            .
          </s-list-item>
          <s-list-item>
            Find <s-text type="strong">Automatic redirection</s-text> and turn
            it off, then save.
          </s-list-item>
          <s-list-item>
            Keep SafeGeo on Ask first so visitors see the popup.
          </s-list-item>
        </s-ordered-list>
        <AppButton
          slot="primary-action"
          commandFor="how-check"
          command="--hide"
          onClick={() => markPrefsChecked()}
        >
          I turned it off
        </AppButton>
      </s-modal>
    </SafePage>
  );
}
